import React, { useRef } from 'react';

import { isPathMatches } from '~/components/app-header/utils/navigation-utils';
import { ChatInputComposer, ChatInputConfig } from '~/components/chat';
import { useIsGeneratedQuestion } from '~/hooks/use-is-generated-question/useIsGeneratedQuestion';
import {
  POSTHOG_TRACKING_SOURCES,
  TrackingMetadata,
} from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useReasonerParams } from '~/views/alfred/research/hooks/useReasonerParams';
import { postClonePublicConversation } from '~/views/alfred/research/services/conversations-service';
import { useChat } from '~/views/alfred/research/use-chat/useChat';

export interface ChatInputProps {
  config?: ChatInputConfig;
  submitCallback?: (message?: string) => void;
  trackingMetadata?: TrackingMetadata;
}

export const ChatDeepAnalysis = ({
  config,
  submitCallback,
  trackingMetadata,
}: ChatInputProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { trackAlfredQuerySent, trackAlfredShare } = usePosthogEvents();
  const isAutoGenerated = useIsGeneratedQuestion(config?.suggestedValue);

  const { conversationId, setReasonerParams } = useReasonerParams();

  const {
    activeConversation,
    postQuestion,
    abortRequest,
    clonePublicConversation,
    appendNewConversation,
    analysisMode,
  } = useChat(state => ({
    activeConversation: state.getActiveConversation(conversationId),
    postQuestion: state.postQuestion,
    abortRequest: state.abortRequest,
    clonePublicConversation: state.clonePublicConversation,
    appendNewConversation: state.appendNewConversation,
    analysisMode: state.analysisMode,
  }));

  const isAlfredPage = isPathMatches(['alfred']);
  const lastTask = activeConversation?.tasks?.at(-1);
  const lastState = lastTask?.states?.at(-1);
  const isLoading =
    isAlfredPage &&
    !!conversationId &&
    (!!lastState?.next || activeConversation?.status === 'Processing');

  const submitHandler = async (
    inputValue: string,
    onSubmitCallbackHandler: () => void
  ) => {
    const question = inputValue.trim();

    if (isLoading || !question) {
      return;
    }

    if (
      activeConversation?.access_level === 'public' ||
      (!activeConversation && conversationId)
    ) {
      const response = await postClonePublicConversation(conversationId);

      if (!response.error) {
        const newConversationId = response.data.conversation_id;

        trackAlfredShare(POSTHOG_TRACKING_SOURCES.ALFRED, 'continue');

        if (!config?.alfredEverywhere) {
          setReasonerParams({
            conversationId: newConversationId,
          });
        }

        const conversation = await clonePublicConversation(newConversationId);

        if (conversation) {
          appendNewConversation(conversation);
        }

        await postQuestion(
          question,
          isAlfredPage && newConversationId ? newConversationId : undefined
        );

        if (trackingMetadata?.source) {
          trackAlfredQuerySent({
            source: trackingMetadata.source,
            type: analysisMode,
            via: trackingMetadata.via,
            isAutoGenerated,
          });
        } else if (isAlfredPage) {
          trackAlfredQuerySent({
            source: POSTHOG_TRACKING_SOURCES.ALFRED,
            type: analysisMode,
            isAutoGenerated,
          });
        }

        return;
      }
    }

    const newConversationId = await postQuestion(
      question,
      isAlfredPage && conversationId ? conversationId : undefined
    );

    if (trackingMetadata?.source) {
      trackAlfredQuerySent({
        source: trackingMetadata.source,
        type: analysisMode,
        via: trackingMetadata.via,
        isAutoGenerated,
      });
    } else if (isAlfredPage) {
      trackAlfredQuerySent({
        source: POSTHOG_TRACKING_SOURCES.ALFRED,
        type: analysisMode,
        isAutoGenerated,
      });
    }

    if (newConversationId && !config?.alfredEverywhere) {
      setReasonerParams({
        conversationId: newConversationId,
      });
    }

    submitCallback?.();
    onSubmitCallbackHandler();
  };

  const onSubmit = (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.KeyboardEvent<HTMLTextAreaElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    event.preventDefault();
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  const onKeyDown = (
    _: React.KeyboardEvent<HTMLElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  return (
    <ChatInputComposer
      ref={inputRef}
      handlers={{
        onSubmit,
        onKeyDown,
        onSelect: () => {},
        onAbort: () => abortRequest(conversationId),
      }}
      config={{
        ...config,
        isCustomizedDisabled: lastState?.status === 'Fatal',
        isLoading,
      }}
    />
  );
};
